import { NetworkStatus } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { getFixedWhereClause } from '@modules/common/utils';
import { useGetAnalysisByTokenLazyQuery } from 'generated/graphql';
import { useCallback } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { parseEQL } from 'v2/core/repository/eql/parser';
import toWhereClause from 'v2/core/services/queryBuilder/toWhereClause';
import { useFromWhereClause } from 'v2/core/services/queryBuilder/useFromWhereClause';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';
import normaliseQuery from 'v2/querybuilder/services/denormalise/normaliseQuery';
import { NormalisedNode } from 'v2/querybuilder/types/querystore';
import {
  denormaliseQuery,
  fixNormalisedNodeFields,
  fixNormalisedNodeValues,
  removeQueryFilterNode,
  trackCustomFilterEvents,
} from '../helpers';
import { QueryBuilderActionType } from '../store/queryBuilderActionType';
import { useQueryBuilderStore } from '../store/useQueryBuilderStore';
import { useSaveFilterIDToValueMapInStore } from './useSaveFilterIDToValueMapInStore';

const fixStaleChildren = (
  rootID: string,
  nodeByID: Record<string, NormalisedNode>,
) => {
  const rootNodeChildren = nodeByID[rootID]?.children;

  const actualChildren = rootNodeChildren?.filter(
    (child) => nodeByID[child]?.parent === rootID,
  );

  nodeByID[rootID].children = actualChildren;
};

interface FetchCustomFilterOptions {
  onError?: () => void;
}

export const useFetchCustomFilter = ({
  onError,
}: FetchCustomFilterOptions = {}) => {
  const {
    tenant: { id: tenantID, timezone },
    id: userID,
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const { data: sourceDetailsMap } = useGetSourceDetailsMap();
  const fromWhereClause = useFromWhereClause();
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const whereClauseInStore = useQueryBuilderStore(
    ({ metadata }) => metadata.whereClause,
  );
  const rootID = useQueryBuilderStore(({ query }) => query.rootID);
  const nodesByID = useQueryBuilderStore(({ query }) => query.nodes);
  const filterIDUnderInspect = useQueryBuilderStore(
    ({ metadata }) => metadata.filterIDUnderInspect,
  );

  useSaveFilterIDToValueMapInStore({
    whereClause: whereClauseInStore,
  });

  const [getAnalysisByToken, { data, error, loading }] =
    useGetAnalysisByTokenLazyQuery({
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    });

  const fetchAnalysis = useCallback(
    async (token: string) => {
      try {
        const resp = await getAnalysisByToken({
          variables: {
            token,
            tenantID,
            userID,
          },
        });
        if (
          Boolean(resp.error) ||
          resp.networkStatus === NetworkStatus.error // Checking for network status, because error is not always present due to a bug in apollo client
        ) {
          onError
            ? onError()
            : showErrorNotification({
                contentComponent: 'Failed to fetch the Custom Filter',
                props: {
                  timeout: 10 * 1000, // 10 seconds
                },
              });
          return;
        }

        if (!resp.data?.getAnalysisByToken) {
          return;
        }

        return {
          eqlExpression: resp.data.getAnalysisByToken.context.eqlExpression,
          title: resp.data.getAnalysisByToken.title,
        };
      } catch {
        // do nothing, already handled
      }
    },
    [getAnalysisByToken, onError, showErrorNotification, tenantID, userID],
  );

  const fetchAndApplyFilters = useCallback(
    async (token?: string) => {
      if (!token) {
        return normaliseQuery(fromWhereClause(EmptyWhereClause, rootID));
      }

      dispatch({
        type: QueryBuilderActionType.SET_METADATA,
        payload: {
          isLoading: true,
        },
      });

      const resp = await fetchAnalysis(token);

      if (!sourceDetailsMap || !resp) {
        dispatch({
          type: QueryBuilderActionType.SET_METADATA,
          payload: {
            isLoading: false,
          },
        });
        return normaliseQuery(fromWhereClause(EmptyWhereClause, rootID));
      }

      const { eqlExpression, title: analysisTitle } = resp;

      const whereClause = parseEQL(
        eqlExpression,
        sourceDetailsMap,
        timezone,
      ).whereClause;

      if (!whereClause) {
        dispatch({
          type: QueryBuilderActionType.SET_METADATA,
          payload: {
            isLoading: false,
          },
        });
        return normaliseQuery(fromWhereClause(EmptyWhereClause, rootID));
      }

      const isActiveFilterIDRoot =
        !filterIDUnderInspect || filterIDUnderInspect === rootID;

      const shouldUpdateNodes = !isActiveFilterIDRoot;

      const nodes = normaliseQuery(
        fromWhereClause(whereClause, isActiveFilterIDRoot ? undefined : rootID),
      );

      fixNormalisedNodeFields(nodes);
      fixNormalisedNodeValues(nodes);

      if (shouldUpdateNodes && filterIDUnderInspect) {
        const customFilters = Object.values(nodes);

        const customFilterRootGroupNode = customFilters.find(
          (filter) => filter.type === 'group' && filter.parent === rootID,
        );

        if (customFilterRootGroupNode) {
          // make the current active root node as parent of the custom filter root group node, and assign custom filter group node as child of parent of current active root node
          const parentIDOfActiveRootNode =
            nodesByID[filterIDUnderInspect]?.parent ?? rootID;
          customFilterRootGroupNode.parent = parentIDOfActiveRootNode;

          if (!nodesByID[parentIDOfActiveRootNode]?.children) {
            nodesByID[parentIDOfActiveRootNode].children = [];
          }
          nodesByID[parentIDOfActiveRootNode].children?.push(
            customFilterRootGroupNode.id,
          );
        } else {
          Object.values(nodes).forEach((node) => {
            const parentIDOfActiveRootNode =
              nodesByID[filterIDUnderInspect]?.parent ?? rootID;

            node.parent = parentIDOfActiveRootNode;
            if (!nodesByID[parentIDOfActiveRootNode]?.children) {
              nodesByID[parentIDOfActiveRootNode].children = [];
            }
            nodesByID[parentIDOfActiveRootNode].children?.push(node.id);
          });
        }
      }

      const updatedNodes = shouldUpdateNodes
        ? { ...nodes, ...nodesByID }
        : nodes;

      if (shouldUpdateNodes) {
        fixStaleChildren(rootID, updatedNodes);
      }

      const emptyFilter =
        filterIDUnderInspect && nodesByID[filterIDUnderInspect];

      if (emptyFilter) {
        const nodesWithoutEmptyFilter = removeQueryFilterNode({
          nodes: updatedNodes,
          node: emptyFilter,
          parentID: emptyFilter.parent,
        });
        dispatch({
          type: QueryBuilderActionType.SET_QUERY,
          payload: {
            nodes: nodesWithoutEmptyFilter,
          },
        });
      } else {
        dispatch({
          type: QueryBuilderActionType.SET_QUERY,
          payload: {
            nodes: updatedNodes,
          },
        });
      }

      const updatedWhereClause = getFixedWhereClause(
        toWhereClause(denormaliseQuery(updatedNodes, rootID)),
      );

      dispatch({
        type: QueryBuilderActionType.SET_METADATA,
        payload: {
          whereClause: updatedWhereClause,
          isLoading: false,
          filterIDUnderInspect: null,
        },
      });

      trackCustomFilterEvents({
        eventName: 'Custom Filter Applied',
        filterName: analysisTitle ?? '',
        eql: eqlExpression,
      });

      return updatedNodes;
    },
    [
      fetchAnalysis,
      dispatch,
      sourceDetailsMap,
      timezone,
      fromWhereClause,
      nodesByID,
      rootID,
      filterIDUnderInspect,
    ],
  );

  const analysis = data?.getAnalysisByToken;

  const getWhereClauseForTheFilter = useCallback(
    async (token?: string) => {
      if (!token) {
        return;
      }

      const resp = await fetchAnalysis(token);

      if (!sourceDetailsMap || !resp) {
        return EmptyWhereClause;
      }

      const whereClause = parseEQL(
        resp.eqlExpression,
        sourceDetailsMap,
        timezone,
      ).whereClause;

      return whereClause;
    },
    [fetchAnalysis, sourceDetailsMap, timezone],
  );

  return [
    fetchAndApplyFilters,
    {
      analysis,
      getWhereClauseForTheFilter,
      loading,
      error,
    },
  ] as const;
};
